// Version-history mode (history spec §UI, web viewer). Replaces the file view
// in the content pane: a version list (relative time + size-delta + "Current")
// on the left, and the selected version's rendered preview OR a diff vs the
// current/previous version on the right, under a banner with Restore (editor+)
// and Back. Read-only for viewers (no Restore). Snapshots come from the same
// history API the old drawer used; text renders through the shared markdown
// pipeline so a preview looks like the real note.

import { useEffect, useMemo, useState } from "react";
import { IconArrowLeft, IconArrowBackUp, IconHistory } from "@tabler/icons-react";
import {
  diffSegments,
  type DiffSegment,
  type FileEntry,
  type HistoryClient,
  type VersionInfo,
  type VersionPreview,
} from "@cairn/core";
import { errorText, type ApiClient } from "../api";
import { useAsyncData } from "../lib/use-async";
import { useDelayedLoading } from "../lib/use-delayed-loading";
import { fullTimestamp, relativeTime, sizeDelta } from "../lib/history-format";
import { Markdown } from "./Markdown";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface HistoryViewProps {
  history: HistoryClient;
  fileId: string;
  canRestore: boolean;
  vaultId: string;
  entries: FileEntry[];
  api: ApiClient;
  onExit: () => void;
}

/** Fetch one version's decoded text, cancel-safe, re-run on id change. Serves
 *  from `cache` (the list-wide prefetch) when present so selecting a version
 *  never re-fetches what dedupe already loaded. */
function useVersionText(
  history: HistoryClient,
  fileId: string,
  versionId: string | null,
  cache?: Map<string, VersionPreview>,
) {
  const [state, setState] = useState<{
    data: VersionPreview | null;
    loading: boolean;
    error: string | null;
  }>({ data: null, loading: false, error: null });
  useEffect(() => {
    if (!versionId) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    const hit = cache?.get(versionId);
    if (hit) {
      setState({ data: hit, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    history
      .preview(fileId, versionId)
      .then((p) => !cancelled && setState({ data: p, loading: false, error: null }))
      .catch((e: unknown) => !cancelled && setState({ data: null, loading: false, error: errorText(e) }));
    return () => {
      cancelled = true;
    };
  }, [history, fileId, versionId, cache]);
  return state;
}

/**
 * Prefetch every version's decoded text so the list can hide "no change"
 * (identical-text) snapshots. Belt-and-suspenders to the server-side no-op
 * gate (decisions/92): legacy vaults and non-edit re-persists (epoch bump,
 * compaction, concurrent identical edits) can still leave same-text snapshots.
 * `sizeBytes` is the CRDT object size, not the text length, so it cannot stand
 * in for text equality — we compare decoded text. Bounded concurrency keeps
 * the burst under the endpoint's request budget.
 */
function useVersionPreviews(history: HistoryClient, fileId: string, list: VersionInfo[]) {
  // The cache carries the exact version set (`forKey`) it was built for, so
  // `ready` is false during the render where the list changed but the fetch
  // hasn't run yet — the list waits behind a skeleton instead of flashing an
  // un-deduped list, then the skeleton, then the deduped list.
  const key = useMemo(() => `${fileId} ${list.map((v) => v.versionId).join(",")}`, [fileId, list]);
  const [state, setState] = useState<{ cache: Map<string, VersionPreview>; forKey: string | null }>({
    cache: new Map(),
    forKey: null,
  });
  useEffect(() => {
    if (list.length === 0) {
      setState({ cache: new Map(), forKey: key });
      return;
    }
    let cancelled = false;
    const next = new Map<string, VersionPreview>();
    const ids = list.map((v) => v.versionId);
    void (async () => {
      const concurrency = Math.min(6, ids.length);
      let i = 0;
      const worker = async () => {
        while (i < ids.length && !cancelled) {
          const id = ids[i++];
          if (id === undefined) continue;
          try {
            next.set(id, await history.preview(fileId, id));
          } catch {
            // Leave absent: an un-fetched version is never collapsed.
          }
        }
      };
      await Promise.all(Array.from({ length: concurrency }, worker));
      if (!cancelled) setState({ cache: next, forKey: key });
    })();
    return () => {
      cancelled = true;
    };
  }, [history, fileId, key, list]);
  return { cache: state.cache, ready: state.forKey === key };
}

function missingNote(missing: string[] | undefined): string | null {
  const n = missing?.length ?? 0;
  if (n === 0) return null;
  return `Some attachments in this version are no longer available${n > 1 ? ` (${n} missing)` : ""}.`;
}

function VersionRow({
  v,
  prev,
  isCurrent,
  active,
  now,
  onSelect,
}: {
  v: VersionInfo;
  prev: VersionInfo | undefined;
  isCurrent: boolean;
  active: boolean;
  now: number;
  onSelect: () => void;
}) {
  const delta = sizeDelta(v.sizeBytes, prev?.sizeBytes);
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-2 rounded-4xl px-3 py-2 text-left text-sm outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring",
        active && "bg-primary/10 hover:bg-primary/10",
      )}
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <span className={cn("truncate", active ? "font-medium text-primary" : "text-foreground")} />
          }
        >
          {relativeTime(v.ts, now)}
        </TooltipTrigger>
        <TooltipContent>{fullTimestamp(v.ts)}</TooltipContent>
      </Tooltip>
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {isCurrent && <Badge variant="secondary">Current</Badge>}
        {delta && (
          <span
            className={cn(
              "font-mono text-xs",
              delta.delta > 0 && "text-emerald-600 dark:text-emerald-400",
              delta.delta < 0 && "text-destructive",
              delta.delta === 0 && "text-muted-foreground",
            )}
          >
            {delta.label}
          </span>
        )}
      </div>
    </button>
  );
}

function DiffText({ segments }: { segments: DiffSegment[] }) {
  return (
    <pre className="markdown min-w-0 max-w-3xl flex-1 rounded-lg border bg-muted/40 p-4 font-mono text-xs break-words whitespace-pre-wrap">
      {segments.map((s, i) =>
        s.op === 0 ? (
          <span key={i} className="text-muted-foreground">
            {s.text}
          </span>
        ) : s.op === 1 ? (
          <ins key={i} className="bg-emerald-500/15 text-emerald-700 no-underline dark:text-emerald-300">
            {s.text}
          </ins>
        ) : (
          <del key={i} className="bg-destructive/15 text-destructive">
            {s.text}
          </del>
        ),
      )}
    </pre>
  );
}

export function HistoryView({
  history,
  fileId,
  canRestore,
  vaultId,
  entries,
  api,
  onExit,
}: HistoryViewProps) {
  const versions = useAsyncData(() => history.list(fileId), [fileId]);
  const raw = useMemo(() => versions.data ?? [], [versions.data]);
  const previews = useVersionPreviews(history, fileId, raw);
  const now = Date.now();

  // Text-distinct list: drop a version whose decoded text equals the next
  // OLDER version's — that keeps the oldest of each identical-text run (the
  // real edit) and hides later re-persists of the same text (the phantom
  // "no change" snapshots the server gate + this dedupe exist to remove). A
  // version whose text hasn't loaded is never collapsed. See decisions/92.
  const list = useMemo(() => {
    const out: VersionInfo[] = [];
    for (let i = 0; i < raw.length; i++) {
      const v = raw[i];
      if (!v) continue;
      const cur = previews.cache.get(v.versionId)?.text;
      const olderV = raw[i + 1];
      const older = olderV ? previews.cache.get(olderV.versionId)?.text : undefined;
      if (cur !== undefined && older !== undefined && cur === older) continue;
      out.push(v);
    }
    return out;
  }, [raw, previews.cache]);

  // Skeleton until both the list and its texts (needed to dedupe) are in.
  const listReady = !!versions.data && previews.ready;
  const showListLoading = useDelayedLoading(!listReady);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mode, setMode] = useState<"preview" | "diff">("preview");
  const [diffBase, setDiffBase] = useState<"current" | "previous">("current");

  // Land on the newest SHOWN version once the deduped list is ready (waiting
  // for `listReady` avoids selecting a version that dedupe then hides).
  useEffect(() => {
    if (!listReady) return;
    const newest = list[0]?.versionId;
    if (newest) setSelectedId((cur) => cur ?? newest);
  }, [list, listReady]);

  const selectedIdx = list.findIndex((v) => v.versionId === selectedId);
  const selectedVersion = selectedIdx >= 0 ? list[selectedIdx] : undefined;
  const currentId = list[0]?.versionId ?? null;
  const previousId = selectedIdx >= 0 ? (list[selectedIdx + 1]?.versionId ?? null) : null;
  const isCurrent = selectedId != null && selectedId === currentId;

  // Diff-base validity: "vs current" is a no-op on the current (newest) version,
  // and "vs previous" has no target on the oldest. Disable the invalid option
  // and auto-switch the active base off one the selected version invalidates so
  // the diff never degenerates into "everything added". If neither is valid
  // (a lone version), there is nothing to compare.
  const canDiffCurrent = !isCurrent;
  const canDiffPrevious = previousId !== null;
  const hasDiffBase = canDiffCurrent || canDiffPrevious;
  useEffect(() => {
    if (mode !== "diff") return;
    if (diffBase === "current" && !canDiffCurrent && canDiffPrevious) setDiffBase("previous");
    else if (diffBase === "previous" && !canDiffPrevious && canDiffCurrent) setDiffBase("current");
  }, [mode, diffBase, canDiffCurrent, canDiffPrevious]);
  const baseId = diffBase === "current" ? currentId : previousId;

  const selected = useVersionText(history, fileId, selectedId, previews.cache);
  // The diff baseline is only fetched in diff mode, and only when it's a
  // different version than the selected one.
  const base = useVersionText(
    history,
    fileId,
    mode === "diff" && baseId !== selectedId ? baseId : null,
    previews.cache,
  );
  const showPreviewLoading = useDelayedLoading(selected.loading);

  const segments = useMemo<DiffSegment[] | null>(() => {
    if (mode !== "diff" || !selected.data || !hasDiffBase) return null;
    if (baseId && baseId !== selectedId && !base.data) return null; // baseline still loading
    const baseText = baseId && baseId !== selectedId ? (base.data?.text ?? "") : "";
    return diffSegments(baseText, selected.data.text);
  }, [mode, selected.data, base.data, baseId, selectedId, hasDiffBase]);

  const [restore, setRestore] = useState<{ busy: boolean; done: boolean; error: string | null; note: string | null }>(
    { busy: false, done: false, error: null, note: null },
  );
  async function doRestore() {
    if (!selectedId) return;
    setRestore({ busy: true, done: false, error: null, note: null });
    try {
      const res = await history.restore(fileId, selectedId);
      setRestore({ busy: false, done: true, error: null, note: missingNote(res.attachmentsMissing) });
    } catch (e) {
      setRestore((r) => ({ ...r, busy: false, error: errorText(e) }));
    }
  }

  if (versions.error) {
    return (
      <div className="grid h-full place-items-center p-6">
        <Alert variant="destructive" className="max-w-md">
          <AlertDescription>Couldn't load version history: {versions.error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (versions.data && raw.length === 0) {
    return (
      <div className="grid h-full place-items-center p-6">
        <Empty className="max-w-md border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <IconHistory />
            </EmptyMedia>
            <EmptyTitle>No history yet</EmptyTitle>
            <EmptyDescription>
              This file has no saved versions. Versions are captured automatically as it's edited.
            </EmptyDescription>
          </EmptyHeader>
          <Button variant="outline" onClick={onExit}>
            <IconArrowLeft data-icon="inline-start" />
            Back to file
          </Button>
        </Empty>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Viewing-history banner */}
      <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-2">
        <IconHistory className="size-4 text-muted-foreground" />
        <span className="text-sm font-medium">Version history</span>
        {selectedVersion && (
          <span className="truncate text-sm text-muted-foreground">
            · viewing {relativeTime(selectedVersion.ts, now)}
            {isCurrent && " (current)"}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {canRestore && (
            <Button
              size="sm"
              disabled={!selectedId || restore.busy || restore.done || isCurrent}
              onClick={() => void doRestore()}
            >
              {restore.busy && <Spinner />}
              <IconArrowBackUp data-icon="inline-start" />
              {restore.done ? "Restored" : "Restore this version"}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onExit}>
            <IconArrowLeft data-icon="inline-start" />
            Back to file
          </Button>
        </div>
      </div>

      {(restore.error || restore.done) && (
        <div className="border-b px-4 py-2">
          {restore.error ? (
            <Alert variant="destructive">
              <AlertDescription>{restore.error}</AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertDescription>
                Restored as a new edit — it merges with any concurrent changes.
                {restore.note ? ` ${restore.note}` : ""} Go back to the file to see it live.
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        {/* Version list */}
        <div className="w-72 shrink-0 overflow-y-auto overscroll-contain border-r p-2">
          {showListLoading && !listReady && (
            <div className="flex flex-col gap-2 p-1">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-9 w-full rounded-4xl" />
              ))}
            </div>
          )}
          {listReady && (
            <ul className="flex flex-col gap-0.5">
              {list.map((v, i) => (
                <li key={v.versionId}>
                  <VersionRow
                    v={v}
                    prev={list[i + 1]}
                    isCurrent={i === 0}
                    active={v.versionId === selectedId}
                    now={now}
                    onSelect={() => {
                      setSelectedId(v.versionId);
                      setRestore({ busy: false, done: false, error: null, note: null });
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Preview / diff */}
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex items-center gap-3 border-b px-4 py-2">
            <Tabs value={mode} onValueChange={(v) => setMode(v as "preview" | "diff")}>
              <TabsList>
                <TabsTrigger value="preview">Preview</TabsTrigger>
                <TabsTrigger value="diff">Diff</TabsTrigger>
              </TabsList>
            </Tabs>
            {/* Diff base is a dependent control: only meaningful in Diff mode. */}
            {mode === "diff" && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">vs</span>
                <Tabs value={diffBase} onValueChange={(v) => setDiffBase(v as "current" | "previous")}>
                  <TabsList>
                    <TabsTrigger value="current" disabled={!canDiffCurrent}>
                      Current
                    </TabsTrigger>
                    <TabsTrigger value="previous" disabled={!canDiffPrevious}>
                      Previous
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-6 lg:p-8">
            {selected.error && (
              <Alert variant="destructive">
                <AlertDescription>Couldn't load this version: {selected.error}</AlertDescription>
              </Alert>
            )}
            {!selected.error && selected.loading && showPreviewLoading && (
              <div className="flex items-center gap-2 p-4 text-muted-foreground">
                <Spinner className="size-4" />
                <span className="font-mono text-xs tracking-wider uppercase">Loading version…</span>
              </div>
            )}
            {selected.data && (
              <div className="flex flex-col gap-4">
                {missingNote(selected.data.attachmentsMissing) && (
                  <Alert>
                    <AlertDescription>{missingNote(selected.data.attachmentsMissing)}</AlertDescription>
                  </Alert>
                )}
                {mode === "preview" ? (
                  <Markdown text={selected.data.text} vaultId={vaultId} entries={entries} api={api} />
                ) : !hasDiffBase ? (
                  <div className="p-4 text-sm text-muted-foreground">
                    Nothing to compare — this is the only version.
                  </div>
                ) : segments ? (
                  <DiffText segments={segments} />
                ) : (
                  <div className="flex items-center gap-2 p-4 text-muted-foreground">
                    <Spinner className="size-4" />
                    <span className="font-mono text-xs tracking-wider uppercase">Computing diff…</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
